import React, { useState } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_UserInterface() {
  const [accordionOpen, setAccordionOpen] = useState(false);
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">
        🎨 Advanced CSS: User Interface Components
      </h1>

      {/* Intro */}
      <p className="tutorial-intro">
        Learn how to create modern, interactive UI components using CSS. This
        tutorial covers buttons, cards, modals, tooltips, and toggles with
        gradients, hover effects, and animations for professional designs.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg,#d1fae5,#6ee7b7)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#065f46" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code>box-shadow</code> and <code>border-radius</code> for depth
            and smoothness.
          </li>
          <li>
            Use <code>transition</code> and <code>transform</code> for smooth
            hover/active effects.
          </li>
          <li>
            Use <code>:hover</code>, <code>:active</code>, and{" "}
            <code>:focus</code> for interactivity.
          </li>
          <li>
            CSS <code>variables</code> make themes easier to manage.
          </li>
          <li>Combine gradients, animations, and shadows for modern UI.</li>
        </ul>
      </div>

      {/* Buttons */}
      <div className="tutorial-section">
        <h2 className="section-title">🔘 Buttons</h2>
        <CodeCopyBlock
          code={`.btn { padding: 10px 20px; border-radius: 8px; background: linear-gradient(90deg,#3b82f6,#9333ea); color: white; border: none; cursor: pointer; transition: transform 0.3s ease, box-shadow 0.3s ease; } .btn:hover { transform: translateY(-3px); box-shadow: 0 8px 20px rgba(0,0,0,0.2); }`}
        />
        <div style={{ display: "flex", gap: "10px", marginBottom: "1rem" }}>
          <button
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              background: "linear-gradient(90deg,#3b82f6,#9333ea)",
              color: "white",
              border: "none",
              cursor: "pointer",
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.transform = "translateY(-3px)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.transform = "translateY(0)")
            }
          >
            Gradient Button
          </button>
          <button
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              background: "#10b981",
              color: "white",
              border: "none",
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#059669";
              e.currentTarget.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#10b981";
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            Solid Button
          </button>
        </div>

        {/* Cards */}
        <h2 className="section-title">📦 Cards</h2>
        <CodeCopyBlock
          code={`.card { padding: 20px; border-radius: 12px; background: white; box-shadow: 0 8px 20px rgba(0,0,0,0.1); transition: transform 0.3s ease; } .card:hover { transform: translateY(-5px); box-shadow: 0 12px 30px rgba(0,0,0,0.15); }`}
        />
        <div
          style={{
            display: "flex",
            gap: "15px",
            flexWrap: "wrap",
            marginBottom: "1rem",
          }}
        >
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                padding: "20px",
                borderRadius: "12px",
                background: "white",
                boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                width: "200px",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px)";
                e.currentTarget.style.boxShadow =
                  "0 12px 30px rgba(0,0,0,0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.1)";
              }}
            >
              <h3>Card {i}</h3>
              <p>Modern card UI with hover effect and shadow depth.</p>
            </div>
          ))}
        </div>

        {/* Toggle Switch */}
        <h2 className="section-title">⚡ Toggle Switch</h2>
        <CodeCopyBlock
          code={`.toggle { position: relative; width: 60px; height: 30px; background: #e5e7eb; border-radius: 15px; cursor: pointer; transition: background 0.3s ease; } .toggle::after { content: ''; position: absolute; width: 26px; height: 26px; background: white; border-radius: 50%; top: 2px; left: 2px; transition: transform 0.3s ease; } .toggle.active { background: #3b82f6; } .toggle.active::after { transform: translateX(30px); }`}
        />
        <div style={{ display: "flex", gap: "10px", marginBottom: "1rem" }}>
          <div
            className="toggle"
            style={{
              position: "relative",
              width: "60px",
              height: "30px",
              background: "#e5e7eb",
              borderRadius: "15px",
              cursor: "pointer",
              transition: "background 0.3s ease",
            }}
            onClick={(e) => e.currentTarget.classList.toggle("active")}
          >
            <div
              style={{
                position: "absolute",
                width: "26px",
                height: "26px",
                background: "white",
                borderRadius: "50%",
                top: "2px",
                left: "2px",
                transition: "transform 0.3s ease",
              }}
            />
          </div>
        </div>

        {/* Tooltip */}
        <h2 className="section-title">💬 Tooltip</h2>
        <CodeCopyBlock
          code={`.tooltip { position: relative; cursor: pointer; } .tooltip:hover::after { content: attr(data-text); position: absolute; bottom: 125%; left: 50%; transform: translateX(-50%); background: #111827; color: white; padding: 6px 10px; border-radius: 5px; white-space: nowrap; opacity: 1; pointer-events: auto; } .tooltip::after { opacity: 0; pointer-events: none; transition: opacity 0.3s ease; }`}
        />
        <div style={{ display: "flex", gap: "15px", marginBottom: "1rem" }}>
          <span
            className="tooltip"
            data-text="I am a tooltip!"
            style={{
              position: "relative",
              cursor: "pointer",
              color: "#3b82f6",
            }}
          >
            Hover me
          </span>
          <span
            className="tooltip"
            data-text="Another tooltip!"
            style={{
              position: "relative",
              cursor: "pointer",
              color: "#10b981",
            }}
          >
            Hover me too
          </span>
        </div>
      </div>

      {/* 1. Animated Loading Spinner */}
      <h3> Loading Spinner</h3>
      <CodeCopyBlock
        code={`.spinner { width: 50px; height: 50px; border: 6px solid #e5e7eb; border-top-color: #3b82f6; border-radius: 50%; animation: spin 1s linear infinite; } @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
      />
      <div style={{ marginBottom: "1rem" }}>
        <div
          className="spinner"
          style={{
            width: "50px",
            height: "50px",
            border: "6px solid #e5e7eb",
            borderTopColor: "#3b82f6",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
      </div>

      {/* 2. Notification / Alert Card */}
      <h3> Notification Card</h3>
      <CodeCopyBlock
        code={`.notification { padding: 15px; background: linear-gradient(90deg,#fbbf24,#f97316); color: white; border-radius: 10px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); }`}
      />
      <div
        style={{
          padding: "15px",
          background: "linear-gradient(90deg,#fbbf24,#f97316)",
          color: "white",
          borderRadius: "10px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          marginBottom: "1rem",
        }}
      >
        🚀 This is an important notification!
      </div>

      {/* 3. Accordion / Collapsible Panel */}
      <h3> Accordion Panel</h3>
      <CodeCopyBlock
        code={`.accordion { background: #3b82f6; color: white; padding: 10px 15px; border-radius: 8px; cursor: pointer; } .panel { background: #e0f2fe; padding: 10px 15px; max-height: 0; overflow: hidden; transition: max-height 0.3s ease; }`}
      />
      <div style={{ marginBottom: "1rem" }}>
        <div
          style={{
            background: "#3b82f6",
            color: "white",
            padding: "10px 15px",
            borderRadius: "8px",
            cursor: "pointer",
          }}
          onClick={() => setAccordionOpen(!accordionOpen)}
        >
          Click to Toggle Accordion
        </div>
        <div
          style={{
            background: "#e0f2fe",
            padding: "10px 15px",
            maxHeight: accordionOpen ? "150px" : "0",
            overflow: "hidden",
            transition: "max-height 0.3s ease",
          }}
        >
          <p>This is the hidden content inside the accordion panel.</p>
        </div>
      </div>

      {/* 4. Progress Bar with Gradient */}
      <h3> Gradient Progress Bar</h3>
      <CodeCopyBlock
        code={`.progress-container { width: 100%; background: #e5e7eb; border-radius: 10px; } .progress-bar { width: 70%; height: 20px; background: linear-gradient(90deg,#3b82f6,#9333ea); border-radius: 10px; transition: width 0.3s ease; }`}
      />
      <div
        style={{
          width: "100%",
          background: "#e5e7eb",
          borderRadius: "10px",
          marginBottom: "1rem",
        }}
      >
        <div
          style={{
            width: "70%",
            height: "20px",
            background: "linear-gradient(90deg,#3b82f6,#9333ea)",
            borderRadius: "10px",
            transition: "width 0.3s ease",
          }}
        />
      </div>

      {/* 5. Fancy Input Field */}
      <h3> Animated Input Field</h3>
      <CodeCopyBlock
        code={`.fancy-input { padding: 10px 15px; border-radius: 8px; border: 2px solid #e5e7eb; transition: border-color 0.3s ease, box-shadow 0.3s ease; } .fancy-input:focus { border-color: #3b82f6; box-shadow: 0 0 8px rgba(59,130,246,0.5); outline: none; }`}
      />
      <input
        type="text"
        placeholder="Type something..."
        style={{
          padding: "10px 15px",
          borderRadius: "8px",
          border: "2px solid #e5e7eb",
          transition: "border-color 0.3s ease, box-shadow 0.3s ease",
          width: "300px",
          marginBottom: "1rem",
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = "#3b82f6";
          e.currentTarget.style.boxShadow = "0 0 8px rgba(59,130,246,0.5)";
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = "#e5e7eb";
          e.currentTarget.style.boxShadow = "none";
        }}
      />

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg,#d1fae5,#6ee7b7)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#065f46" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Create <strong>animated modals</strong> with smooth fade-in/out.
          </li>
          <li>
            Combine <strong>progress bars + notifications</strong> for
            dashboards.
          </li>
          <li>
            Make <strong>fully responsive UI</strong> for mobile and desktop.
          </li>
          <li>
            Experiment with <strong>advanced hover + gradient combos</strong>{" "}
            for unique designs.
          </li>
        </ul>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg,#d1fae5,#6ee7b7)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#065f46" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Create <strong>animated modals and popups</strong> with CSS
            transitions.
          </li>
          <li>
            Use <strong>CSS variables</strong> for dark/light themes.
          </li>
          <li>
            Combine <strong>gradients + shadows + animations</strong> for
            futuristic UI designs.
          </li>
          <li>
            Experiment with{" "}
            <strong>interactive toggle panels and accordions</strong>.
          </li>
        </ul>
      </div>
    </div>
  );
}
